import { DomPortalOutlet, TemplatePortal } from '@angular/cdk/portal';
import { DOCUMENT } from '@angular/common';
import {
  Directive,
  inject,
  OnInit,
  OnDestroy,
  TemplateRef,
  ViewContainerRef,
  ApplicationRef,
  Injector,
  DestroyRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { MdwEmojiService } from './mdw-emoji-service';

@Directive({
  selector: '[mdwEmojiPortalHost]',
})
export class EmojiPortalHostDirective implements OnInit, OnDestroy {
  private document = inject(DOCUMENT);
  private templateRef = inject(TemplateRef);
  private viewContainerRef = inject(ViewContainerRef);
  private appRef = inject(ApplicationRef);
  private injector = inject(Injector);
  private emojiService = inject(MdwEmojiService);
  private destroyRef = inject(DestroyRef);

  private portalOutlet: DomPortalOutlet | null = null;
  private containerElement: HTMLElement | null = null;
  private portal: TemplatePortal | null = null;

  public ngOnInit(): void {
    // Remove any orphaned containers from previous instances
    this.document.querySelectorAll('.emoji-portal-container').forEach(el => el.remove());

    // Create container element at body level with popover attribute
    // This ensures it renders in the browser's top layer, above CDK overlays
    this.containerElement = this.document.createElement('div');
    this.containerElement.classList.add('emoji-portal-container');
    this.containerElement.setAttribute('popover', 'manual');
    this.document.body.appendChild(this.containerElement);

    // Create portal outlet
    this.portalOutlet = new DomPortalOutlet(this.containerElement, this.appRef, this.injector);

    // Create and attach template portal
    this.portal = new TemplatePortal(this.templateRef, this.viewContainerRef);
    this.portalOutlet.attach(this.portal);

    // Show the popover immediately and keep it shown
    // The visibility is controlled by the .show class on the inner emoji-container
    this.containerElement.showPopover();

    // Subscribe to emoji panel visibility to manage popover state
    // When showing, we hide and re-show to move the popover to the top of the top-layer stack
    // This ensures it appears above any CDK dialogs that were opened after this popover
    // Note: hidePopover/showPopover only toggles visibility - it does NOT remove from DOM
    // or cause any re-rendering of the emoji picker content
    this.emojiService.showEmojiPanel$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(show => {
      if (show && this.containerElement) {
        this.containerElement.hidePopover();
        this.containerElement.showPopover();
      }
    });
  }

  public ngOnDestroy(): void {
    this.containerElement?.hidePopover();
    this.portalOutlet?.dispose();
    this.containerElement?.remove();
  }
}
